/**
 * TileDeliveryInfo is a component to display tile delivery information for a selected fullfillment method
 *
 * @module views/components/TileDeliveryInfo
 * @memberof -Common
 */
import './TileDeliveryInfo.scss';

import React from 'react';

import PropTypes from 'prop-types';

import Text from '@ulta/core/components/Text/Text';
import { handleEmptyObjects } from '@ulta/core/utils/handleEmptyObjects/handleEmptyObjects';

import Button from '@ulta/modules/Button/Button';
import Tag from '@ulta/modules/Tag/Tag';

import * as utils from './TileDeliveryInfo';
/**
 * Represents a TileDeliveryInfo component
 *
 * @method
 * @param {TileDeliveryInfoProps} props - React properties passed from composition
 * @returns TileDeliveryInfo
 */
export const TileDeliveryInfo = function( props ){
  const {
    location,
    speed,
    secondarySpeed,
    stockStatus,
    stockNearby,
    linkAction,
    invokeAction,
    productId,
    skuId
  } = props;

  if( !location ){
    return null;
  }

  return (
    <div className='TileDeliveryInfo'>
      <div className='TileDeliveryInfo__left'>
        <Text
          textStyle='body-2'
        >
          { location }
        </Text>
        { speed &&
          <Text
            textStyle='body-2'
            color='neutral-500'
          >
            { speed }
          </Text>
        }
        { secondarySpeed &&
          <Text
            textStyle='body-2'
            color='neutral-500'
          >
            { secondarySpeed }
          </Text>
        }
        { stockStatus?.message &&
          <Tag
            message={ stockStatus.message }
            variant='utilityCompact'
            style={ stockStatus.markerColor }
          />
        }
        { stockNearby?.message && // future BOPIS feature to denote stock at a nearby store
          <Tag
            message={ stockNearby.message }
            variant='utility'
            style={ stockNearby.markerColor }
          />
        }
      </div>
      <div className='TileDeliveryInfo__right'>
        { linkAction &&
        <Button
          label={ linkAction.label }
          variant='link'
          onClick={ () => utils.handleLinkAction( { linkAction, productId, skuId }, { invokeAction } ) }
        />
        }
      </div>
    </div>
  );
};

/**
 * Handles the link action by invoking it with the required parameters.
 *
 * @param {Object} data - contains the action and parameters.
 * @param {Object} data.linkAction - contian the link action.
 * @param {string} data.productId - product Id.
 * @param {string} data.skuId -  product SKU Id.
 * @param {Object} methods - contains utility functions.
 * @param {Function} methods.invokeAction - A function to invoke the actions.
 */
export const handleLinkAction = ( data, methods ) => {
  const { linkAction, productId, skuId } = handleEmptyObjects( data );
  const { invokeAction } = methods;
  if( !linkAction ){
    return;
  }
  invokeAction( {
    ...linkAction,
    variables : {
      moduleParams: {
        productId,
        skuId
      }
    }
  } );
};

/**
 * Property type definitions
 * @typedef TileDeliveryInfoProps
 * @type {object}
 * @property {string} location - fullfillment location
 * @property {string} speed - fullfillment timeframe
 * @property {string} secondarySpeed - secondary fullfillment timeframe
 * @property {object} stockStatus - stock level status Tag message and marker color
 * @property {object} stockNearby - stock availability at a nearby store Tag message and marker color
 * @property {object} linkAction - link action object for 'change' button
 */
export const propTypes =  {
  location: PropTypes.string,
  speed: PropTypes.string,
  secondarySpeed: PropTypes.string,
  stockStatus: PropTypes.object,
  stockNearby: PropTypes.object,
  linkAction: PropTypes.object
};


TileDeliveryInfo.propTypes = propTypes;

export default TileDeliveryInfo;
